<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">🔧 Tools & API-integrationer</h1>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Definiera externa API:er som dina AI-agenter kan anropa under en konversation
        </p>
      </div>
      <div class="mt-4 sm:mt-0 flex items-center gap-2">
        <button
          @click="activeTab = 'docs'"
          v-if="activeTab !== 'docs'"
          class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium"
        >
          📖 Guide
        </button>
        <button
          @click="showCreateModal = true"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center"
        >
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
          </svg>
          Skapa nytt Tool
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="border-b border-gray-200 dark:border-gray-700">
      <nav class="flex flex-wrap -mb-px gap-x-6">
        <button
          @click="activeTab = 'tools'"
          :class="[
            'py-2 px-1 border-b-2 text-sm font-medium transition-colors',
            activeTab === 'tools'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          ]"
        >
          Mina tools
        </button>
        <button
          @click="activeTab = 'templates'"
          :class="[
            'py-2 px-1 border-b-2 text-sm font-medium transition-colors',
            activeTab === 'templates'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          ]"
        >
          🚀 Demo-mallar
        </button>
        <button
          @click="activeTab = 'docs'"
          :class="[
            'py-2 px-1 border-b-2 text-sm font-medium transition-colors',
            activeTab === 'docs'
              ? 'border-blue-600 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          ]"
        >
          📖 Guide & dokumentation
        </button>
      </nav>
    </div>

    <!-- TAB: TOOLS LIST -->
    <div v-if="activeTab === 'tools'">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>

    <!-- Tools List -->
    <div v-else-if="tools.length > 0" class="space-y-4">
      <div
        v-for="tool in tools"
        :key="tool.tool_id"
        class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <div class="flex items-center flex-wrap gap-2 mb-2">
              <span class="text-2xl">🔵</span>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">{{ tool.display_name }}</h3>
              <span class="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                Aktiv
              </span>
            </div>
            
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">{{ tool.description }}</p>
            
            <!-- API Details -->
            <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 mb-3 font-mono text-sm">
              <div class="flex items-center space-x-2">
                <span :class="[
                  'px-2 py-0.5 rounded text-xs font-bold',
                  tool.api_method === 'GET' ? 'bg-blue-100 text-blue-700' :
                  tool.api_method === 'POST' ? 'bg-green-100 text-green-700' :
                  tool.api_method === 'PUT' ? 'bg-yellow-100 text-yellow-700' :
                  tool.api_method === 'DELETE' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-700'
                ]">
                  {{ tool.api_method }}
                </span>
                <span class="text-gray-700 dark:text-gray-300 break-all">{{ tool.api_url }}</span>
              </div>
            </div>

            <!-- Parameters -->
            <div v-if="tool.parameters && tool.parameters.length > 0" class="mb-3">
              <p class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Parametrar:</p>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="param in tool.parameters"
                  :key="param.parameter_name"
                  class="px-2 py-1 text-xs rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                >
                  {{ param.parameter_name }}
                  <span class="text-gray-500">({{ param.parameter_type }})</span>
                  <span v-if="param.is_required" class="text-red-500">*</span>
                </span>
              </div>
            </div>

            <!-- Assigned Agents -->
            <div class="mb-3">
              <span class="text-xs text-gray-500 dark:text-gray-400">Tilldelad till:</span>
              <span
                v-for="agentId in tool.assigned_agent_ids"
                :key="agentId"
                class="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
              >
                {{ getAgentName(agentId) }}
              </span>
            </div>
            
            <!-- Stats -->
            <div class="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
              <span>Använd {{ tool.usage_count_7d || 0 }} gånger senaste 7 dagarna</span>
              <span>•</span>
              <span>Skapad {{ formatDate(tool.created_at) }}</span>
            </div>
          </div>
          
          <!-- Actions -->
          <div class="flex items-center space-x-2 ml-4">
            <button
              @click="editTool(tool)"
              class="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
              title="Redigera"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
              </svg>
            </button>
            <button
              @click="deleteTool(tool)"
              class="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
              title="Ta bort"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
      <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
      </svg>
      <h3 class="mt-4 text-lg font-medium text-gray-900 dark:text-white">Inga tools ännu</h3>
      <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Skapa ett tool för att låta dina AI-agenter anropa externa API:er.
      </p>
      <div class="mt-4 flex flex-wrap justify-center gap-2">
        <button
          @click="showCreateModal = true"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          Skapa första tool
        </button>
        <button
          @click="activeTab = 'templates'"
          class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium"
        >
          🚀 Använd en demo-mall
        </button>
        <button
          @click="activeTab = 'docs'"
          class="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium"
        >
          📖 Läs guiden
        </button>
      </div>
    </div>
    </div>
    <!-- /TAB: TOOLS LIST -->

    <!-- TAB: TEMPLATES -->
    <div v-if="activeTab === 'templates'" class="space-y-6">
      <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
        <h2 class="text-lg font-semibold text-blue-900 dark:text-blue-200 flex items-center gap-2">
          🚀 Demo-mallar du kan klona direkt
        </h2>
        <p class="mt-1 text-sm text-blue-800 dark:text-blue-300">
          Klicka på en mall för att förfylla formuläret med ett färdigt, fungerande exempel. Mallarna pekar på publika, gratis API:er — du kan testa dem omedelbart utan API-nyckel. Du kan ändra alla fält innan du sparar.
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          v-for="tpl in demoTemplates"
          :key="tpl.name"
          class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5 flex flex-col"
        >
          <div class="flex items-start gap-3">
            <span class="text-3xl">{{ tpl.emoji }}</span>
            <div class="flex-1">
              <h3 class="font-semibold text-gray-900 dark:text-white">{{ tpl.displayName }}</h3>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ tpl.tagline }}</p>
            </div>
          </div>

          <p class="text-sm text-gray-600 dark:text-gray-300 mt-3">{{ tpl.description }}</p>

          <div class="mt-3 bg-gray-50 dark:bg-gray-900/50 rounded p-2 font-mono text-xs flex items-center gap-2 flex-wrap">
            <span class="px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700">{{ tpl.apiMethod }}</span>
            <span class="text-gray-700 dark:text-gray-300 break-all">{{ tpl.apiUrl }}</span>
          </div>

          <div v-if="tpl.parameters.length > 0" class="mt-2 flex flex-wrap gap-1">
            <span
              v-for="p in tpl.parameters"
              :key="p.name"
              class="px-2 py-0.5 text-xs rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
            >
              {{ p.name }} <span class="opacity-60">({{ p.type }})</span>
            </span>
          </div>

          <div class="mt-3 text-xs text-gray-500 dark:text-gray-400">
            <span class="font-semibold">Exempelfråga:</span> "{{ tpl.exampleQuery }}"
          </div>

          <button
            @click="useTemplate(tpl)"
            class="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            Klona denna mall
          </button>
        </div>
      </div>

      <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 text-sm text-amber-800 dark:text-amber-200">
        <strong>Tips:</strong> Efter att du klonat en mall, tilldela tool:et till minst en agent i formuläret och spara. Agenten kommer då börja anropa API:et automatiskt när en användares fråga matchar beskrivningen.
      </div>
    </div>
    <!-- /TAB: TEMPLATES -->

    <!-- TAB: DOCS -->
    <div v-if="activeTab === 'docs'" class="space-y-6">
      <!-- Intro -->
      <section class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 class="text-xl font-bold text-gray-900 dark:text-white">📖 Så fungerar tools</h2>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Ett <strong>tool</strong> är ett externt HTTP-API som din AI-agent får anropa mitt i en konversation för att hämta eller skicka data i realtid. Istället för att bara svara från kunskapsbasen kan agenten t.ex. slå upp en bokning, kolla vädret, eller skicka en supportbiljett — och sen presentera svaret naturligt på svenska för kunden.
        </p>
        <ul class="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-300 list-disc list-inside">
          <li>Du beskriver verktyget i ord (när det ska användas) → AI:n bestämmer själv när det är dags att anropa det.</li>
          <li>Du anger en URL med <code class="px-1 py-0.5 bg-gray-100 dark:bg-gray-900 rounded text-xs">{platshållare}</code> → AI:n fyller i värdena från kundens meddelande.</li>
          <li>Svaret från API:et matas tillbaka till AI:n, som formulerar ett svar på svenska.</li>
          <li>Varje anrop loggas i <em>tool_execution_logs</em> med status, latens och fel om något gick snett.</li>
        </ul>
      </section>

      <!-- Step by step -->
      <section class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white">🛠️ Skapa ett tool — steg för steg</h2>

        <div class="mt-4 space-y-5 text-sm">
          <div>
            <h3 class="font-semibold text-gray-900 dark:text-white">1. Grundinformation</h3>
            <ul class="mt-1 ml-5 list-disc text-gray-600 dark:text-gray-300 space-y-1">
              <li><strong>Tool-namn (tekniskt):</strong> bara små bokstäver, siffror och underscore, t.ex. <code class="px-1 bg-gray-100 dark:bg-gray-900 rounded">get_booking_status</code>. Detta används internt av AI:n.</li>
              <li><strong>Visningsnamn:</strong> människovänligt, t.ex. "Hämta bokningsstatus". Visas i admin.</li>
              <li><strong>Beskrivning:</strong> <em>viktigast av allt</em>. AI:n läser detta för att veta <em>när</em> verktyget ska användas. Skriv konkret, t.ex. "Använd när kunden frågar om status på en specifik bokning och uppger ett bokningsnummer".</li>
            </ul>
          </div>

          <div>
            <h3 class="font-semibold text-gray-900 dark:text-white">2. API-konfiguration</h3>
            <ul class="mt-1 ml-5 list-disc text-gray-600 dark:text-gray-300 space-y-1">
              <li><strong>Metod:</strong> GET för uppslag, POST för att skapa/skicka, PUT/PATCH för uppdatering, DELETE för att ta bort.</li>
              <li><strong>API URL:</strong> kan innehålla platshållare i klammerparenteser, t.ex. <code class="px-1 bg-gray-100 dark:bg-gray-900 rounded">https://api.exempel.se/bokningar/&#123;booking_id&#125;</code>. Vid anropet ersätts <code>&#123;booking_id&#125;</code> med värdet AI:n extraherat ur användarens meddelande.</li>
              <li><strong>Headers (JSON):</strong> t.ex. <code class="px-1 bg-gray-100 dark:bg-gray-900 rounded">&#123;"Authorization": "Bearer DIN_NYCKEL"&#125;</code>. Auth-nycklar krypteras vid lagring.</li>
            </ul>
          </div>

          <div>
            <h3 class="font-semibold text-gray-900 dark:text-white">3. Parametrar (vad AI:n ska extrahera)</h3>
            <p class="mt-1 text-gray-600 dark:text-gray-300">Varje parameter är ett fält som AI:n ska plocka ut från användarens meddelande. Internt blir detta ett JSON Schema som skickas till LLM:en:</p>
            <ul class="mt-2 ml-5 list-disc text-gray-600 dark:text-gray-300 space-y-1">
              <li><strong>Namn:</strong> exakt samma som platshållaren i URL:en, t.ex. <code class="px-1 bg-gray-100 dark:bg-gray-900 rounded">booking_id</code>.</li>
              <li><strong>Typ:</strong> <code>string</code>, <code>number</code> eller <code>boolean</code>. Hjälper AI:n att formatera värdet rätt.</li>
              <li><strong>Beskrivning:</strong> berätta för AI:n vad fältet betyder, t.ex. "Bokningens id, ett 6-siffrigt nummer".</li>
              <li><strong>Obligatorisk:</strong> kryssa i om anropet inte kan göras utan värdet. AI:n kommer då fråga kunden om det saknas.</li>
            </ul>
          </div>

          <div>
            <h3 class="font-semibold text-gray-900 dark:text-white">4. Tilldela till agenter</h3>
            <p class="mt-1 text-gray-600 dark:text-gray-300">Bocka i vilka av dina agenter som ska få använda detta tool. Bara dessa agenter ser verktyget i sin function-calling-meny under en konversation.</p>
          </div>

          <div>
            <h3 class="font-semibold text-gray-900 dark:text-white">5. Testa</h3>
            <p class="mt-1 text-gray-600 dark:text-gray-300">Öppna chat-widgeten och ställ en fråga som matchar tool-beskrivningen. Gå sen tillbaka hit och titta i listan — du ser hur många gånger det använts senaste 7 dagarna.</p>
          </div>
        </div>
      </section>

      <!-- Worked example -->
      <section class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white">💡 Komplett exempel — Boknings-uppslag</h2>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
          Scenario: en kund skriver i chatten <em>"Hej, kan ni kolla status på bokning 42?"</em>. Vi vill att agenten ska slå upp bokningen mot ett internt API och svara med detaljerna.
        </p>

        <div class="mt-4 overflow-x-auto">
          <table class="min-w-full text-sm">
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr><td class="py-2 pr-4 font-medium text-gray-700 dark:text-gray-200 align-top">Tool-namn</td><td class="py-2 font-mono text-xs text-gray-900 dark:text-gray-100">get_booking_by_id</td></tr>
              <tr><td class="py-2 pr-4 font-medium text-gray-700 dark:text-gray-200 align-top">Visningsnamn</td><td class="py-2 text-gray-900 dark:text-gray-100">Hämta bokning via bokningsnummer</td></tr>
              <tr><td class="py-2 pr-4 font-medium text-gray-700 dark:text-gray-200 align-top">Beskrivning</td><td class="py-2 text-gray-900 dark:text-gray-100">Använd när kunden frågar om status, detaljer eller historik för en specifik bokning och uppger ett bokningsnummer (t.ex. "kolla bokning 42" eller "vad hände med min bokning 1042").</td></tr>
              <tr><td class="py-2 pr-4 font-medium text-gray-700 dark:text-gray-200 align-top">Metod</td><td class="py-2 font-mono text-xs">GET</td></tr>
              <tr><td class="py-2 pr-4 font-medium text-gray-700 dark:text-gray-200 align-top">URL</td><td class="py-2 font-mono text-xs break-all">https://jsonplaceholder.typicode.com/posts/&#123;booking_id&#125;</td></tr>
              <tr><td class="py-2 pr-4 font-medium text-gray-700 dark:text-gray-200 align-top">Headers</td><td class="py-2 font-mono text-xs">&#123;&#125;</td></tr>
              <tr><td class="py-2 pr-4 font-medium text-gray-700 dark:text-gray-200 align-top">Parameter</td><td class="py-2 text-gray-900 dark:text-gray-100">
                <code class="font-mono text-xs">booking_id</code> · typ <code>number</code> · obligatorisk · "Bokningens unika id-nummer"
              </td></tr>
            </tbody>
          </table>
        </div>

        <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <div class="bg-gray-50 dark:bg-gray-900/50 rounded p-3">
            <div class="font-semibold text-gray-700 dark:text-gray-200 mb-1">Vad agenten ser</div>
            <div class="font-mono text-gray-600 dark:text-gray-300">Användare: "kolla bokning 42"<br/>→ AI extraherar booking_id=42<br/>→ GET .../posts/42<br/>→ &#123;"id":42,"title":"...","body":"..."&#125;</div>
          </div>
          <div class="bg-gray-50 dark:bg-gray-900/50 rounded p-3">
            <div class="font-semibold text-gray-700 dark:text-gray-200 mb-1">Vad kunden ser</div>
            <div class="text-gray-600 dark:text-gray-300">"Här är detaljerna för bokning 42: rubrik 'X', anteckning 'Y'. Vill du att jag gör något mer med den?"</div>
          </div>
        </div>

        <button
          @click="useTemplate(demoTemplates[0])"
          class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          Skapa detta exempel nu →
        </button>
      </section>

      <!-- Placeholders -->
      <section class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white">🔗 Platshållare — så fyller du i värden</h2>
        <ul class="mt-3 space-y-2 text-sm text-gray-600 dark:text-gray-300">
          <li><strong>I URL:en:</strong> använd <code class="px-1 bg-gray-100 dark:bg-gray-900 rounded">&#123;param_name&#125;</code> (enkla klammerparenteser). Ersätts med URL-encodat värde.</li>
          <li><strong>I headers eller body:</strong> använd <code class="px-1 bg-gray-100 dark:bg-gray-900 rounded">&#123;&#123;param_name&#125;&#125;</code> (dubbla klammerparenteser). Ersätts utan encoding.</li>
          <li>För POST/PUT/PATCH utan body-mall skickas alla parametrar automatiskt som JSON-body.</li>
        </ul>
      </section>

      <!-- Troubleshooting -->
      <section class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white">🐛 Felsökning</h2>
        <div class="mt-3 space-y-3 text-sm text-gray-600 dark:text-gray-300">
          <div>
            <strong class="text-gray-900 dark:text-white">AI:n anropar aldrig mitt tool</strong>
            <p>Beskrivningen är för vag. Skriv vilka exakta scenarier som ska trigga det, och vilka nyckelord kunden brukar använda. Kontrollera även att tool:et är tilldelat till rätt agent.</p>
          </div>
          <div>
            <strong class="text-gray-900 dark:text-white">"Missing required parameter"</strong>
            <p>AI:n hittade inte värdet i kundens meddelande. Antingen är beskrivningen av parametern otydlig, eller så bör fältet markeras som <em>icke</em>-obligatoriskt med ett default-värde.</p>
          </div>
          <div>
            <strong class="text-gray-900 dark:text-white">HTTP 401 / 403</strong>
            <p>Auth-headern är felaktig. Dubbelkolla att API-nyckeln är giltig och att header-namnet matchar (oftast <code>Authorization: Bearer ...</code>).</p>
          </div>
          <div>
            <strong class="text-gray-900 dark:text-white">Timeout</strong>
            <p>API:et svarar inte inom 10s. Höj timeout via "Redigera" om endpoint:en är långsam, eller kontrollera om mottagar-systemet är nere.</p>
          </div>
          <div>
            <strong class="text-gray-900 dark:text-white">Se exakta loggar per anrop</strong>
            <p>Varje anrop sparas i <code>tool_execution_logs</code>-tabellen i D1 med parametrar, statuskod, latens och fullständigt svar. Kontakta support om du behöver utdrag.</p>
          </div>
        </div>
      </section>

      <!-- Security -->
      <section class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 class="text-lg font-bold text-gray-900 dark:text-white">🔒 Säkerhet</h2>
        <ul class="mt-3 space-y-1 text-sm text-gray-600 dark:text-gray-300 list-disc list-inside">
          <li>API-nycklar i headers krypteras vid lagring och returneras aldrig i klartext via API:et.</li>
          <li>Interna nätverksadresser (localhost, 127.x, 169.254.x) blockeras för att förhindra SSRF.</li>
          <li>Rate-limit per företag förhindrar missbruk.</li>
          <li>Tools körs alltid inom kontexten av det företag som äger dem — andra företags agenter ser dem inte.</li>
        </ul>
      </section>
    </div>
    <!-- /TAB: DOCS -->

    <!-- Create/Edit Modal -->
    <Teleport to="body">
      <div
        v-if="showCreateModal"
        class="fixed inset-0 z-50 overflow-y-auto"
        @click.self="closeModal"
      >
        <div class="flex items-center justify-center min-h-screen px-4 py-8">
          <div class="fixed inset-0 bg-black bg-opacity-50"></div>
          <div class="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 class="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {{ editingTool ? 'Redigera Tool' : 'Skapa nytt Tool' }}
            </h2>
            
            <form @submit.prevent="saveTool" class="space-y-4">
              <!-- Basic Info -->
              <div class="space-y-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <h3 class="font-semibold text-gray-900 dark:text-white">📋 Grundinformation</h3>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tool-namn (tekniskt) *
                  </label>
                  <input
                    v-model="form.name"
                    type="text"
                    required
                    :disabled="!!editingTool"
                    pattern="[a-z0-9_]+"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                    placeholder="get_booking_status"
                  />
                  <p class="mt-1 text-xs text-gray-500">Endast a-z, 0-9 och underscore</p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Visningsnamn *
                  </label>
                  <input
                    v-model="form.displayName"
                    type="text"
                    required
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Hämta bokningsstatus"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Beskrivning *
                  </label>
                  <textarea
                    v-model="form.description"
                    required
                    rows="2"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="När AI ska använda detta tool, t.ex: 'Använd när kunden frågar om sin bokning'"
                  ></textarea>
                </div>
              </div>

              <!-- API Configuration -->
              <div class="space-y-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <h3 class="font-semibold text-gray-900 dark:text-white">🌐 API Konfiguration</h3>
                
                <div class="grid grid-cols-4 gap-2">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Metod *
                    </label>
                    <select
                      v-model="form.apiMethod"
                      required
                      class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="PATCH">PATCH</option>
                      <option value="DELETE">DELETE</option>
                    </select>
                  </div>
                  <div class="col-span-3">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      API URL *
                    </label>
                    <input
                      v-model="form.apiUrl"
                      type="url"
                      required
                      class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                      placeholder="https://api.example.com/bookings/{id}"
                    />
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Headers (JSON)
                  </label>
                  <textarea
                    v-model="headersText"
                    rows="2"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
                    placeholder='{"Authorization": "Bearer YOUR_API_KEY"}'
                  ></textarea>
                </div>
              </div>

              <!-- Parameters -->
              <div class="space-y-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <div class="flex items-center justify-between">
                  <h3 class="font-semibold text-gray-900 dark:text-white">⚙️ Parametrar</h3>
                  <button
                    type="button"
                    @click="addParameter"
                    class="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50"
                  >
                    + Lägg till
                  </button>
                </div>
                
                <div v-for="(param, index) in form.parameters" :key="index" class="flex gap-2 items-start p-3 bg-white dark:bg-gray-800 rounded">
                  <div class="flex-1 grid grid-cols-2 gap-2">
                    <input
                      v-model="param.name"
                      type="text"
                      placeholder="Namn (t.ex. booking_id)"
                      class="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <select
                      v-model="param.type"
                      class="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="string">String</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                    </select>
                    <input
                      v-model="param.description"
                      type="text"
                      placeholder="Beskrivning"
                      class="col-span-2 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                    <label class="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <input
                        v-model="param.isRequired"
                        type="checkbox"
                        class="mr-2"
                      />
                      Obligatorisk
                    </label>
                  </div>
                  <button
                    type="button"
                    @click="removeParameter(index)"
                    class="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Agent Assignment -->
              <div class="space-y-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <h3 class="font-semibold text-gray-900 dark:text-white">🤖 Tilldela till agenter *</h3>
                <div class="space-y-2 max-h-48 overflow-y-auto">
                  <label
                    v-for="agent in agents"
                    :key="agent.id"
                    class="flex items-center space-x-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      :value="agent.id"
                      v-model="form.agentIds"
                      class="w-4 h-4"
                    />
                    <span class="text-sm text-gray-900 dark:text-white">{{ agent.name }}</span>
                  </label>
                </div>
              </div>
              
              <div class="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  @click="closeModal"
                  class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  :disabled="saving || form.agentIds.length === 0"
                  class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {{ saving ? 'Sparar...' : (editingTool ? 'Uppdatera' : 'Skapa') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { toolsService, type Tool, type ToolParameter } from '../services/tools'
import { agentsService } from '../services/agents'

interface Agent {
  id: number
  name: string
}

const loading = ref(true)
const saving = ref(false)
const showCreateModal = ref(false)
const tools = ref<Tool[]>([])
const editingTool = ref<Tool | null>(null)
const agents = ref<Agent[]>([])
const headersText = ref('{}')
const activeTab = ref<'tools' | 'templates' | 'docs'>('tools')

// Demo templates — pekar på publika gratis-API:er, inga nycklar krävs.
// Klona och spara i ditt egna company-utrymme via knappen.
interface DemoTemplate {
  emoji: string
  name: string
  displayName: string
  tagline: string
  description: string
  apiMethod: string
  apiUrl: string
  exampleQuery: string
  parameters: Array<{ name: string; type: string; description: string; isRequired: boolean }>
}

const demoTemplates: DemoTemplate[] = [
  {
    emoji: '📦',
    name: 'demo_booking_lookup',
    displayName: 'Boknings-uppslag (demo)',
    tagline: 'GET en bokning via id-nummer',
    description: 'Använd när kunden frågar om status, detaljer eller historik för en specifik bokning och uppger ett boknings-id (t.ex. "kolla bokning 42"). Demon använder JSONPlaceholder som returnerar en mock-bokning för id 1–100.',
    apiMethod: 'GET',
    apiUrl: 'https://jsonplaceholder.typicode.com/posts/{booking_id}',
    exampleQuery: 'Kan ni kolla status på bokning 42?',
    parameters: [
      { name: 'booking_id', type: 'number', description: 'Bokningens unika id-nummer (1-100 för demo)', isRequired: true }
    ]
  },
  {
    emoji: '🌤️',
    name: 'demo_weather_lookup',
    displayName: 'Väderkoll (demo)',
    tagline: 'Aktuellt väder per koordinater',
    description: 'Använd när kunden frågar om vädret på en specifik plats. Demon använder Open-Meteo (gratis, ingen nyckel). Be AI:n omvandla ortsnamn till latitud/longitud, eller skapa ett separat geo-tool först. Standard: Stockholm.',
    apiMethod: 'GET',
    apiUrl: 'https://api.open-meteo.com/v1/forecast?latitude={latitude}&longitude={longitude}&current_weather=true',
    exampleQuery: 'Hur är vädret i Stockholm just nu?',
    parameters: [
      { name: 'latitude', type: 'number', description: 'Latitud för platsen, t.ex. 59.33 för Stockholm', isRequired: true },
      { name: 'longitude', type: 'number', description: 'Longitud för platsen, t.ex. 18.07 för Stockholm', isRequired: true }
    ]
  },
  {
    emoji: '💱',
    name: 'demo_exchange_rate',
    displayName: 'Valutakurs (demo)',
    tagline: 'Växelkurser i realtid mot en baseringsvaluta',
    description: 'Använd när kunden frågar om växelkurs eller vill omvandla pengar mellan valutor. Returnerar alla kurser mot bas-valutan. Demon använder open.er-api.com (gratis, ingen nyckel).',
    apiMethod: 'GET',
    apiUrl: 'https://open.er-api.com/v6/latest/{base_currency}',
    exampleQuery: 'Vad är växelkursen från SEK till EUR?',
    parameters: [
      { name: 'base_currency', type: 'string', description: 'ISO-kod för basvaluta i versaler, t.ex. SEK, USD, EUR', isRequired: true }
    ]
  },
  {
    emoji: '📮',
    name: 'demo_postal_lookup',
    displayName: 'Postnummer-uppslag (demo)',
    tagline: 'Slå upp svensk ort via postnummer',
    description: 'Använd när kunden uppger ett svenskt postnummer och du behöver veta vilken ort det tillhör. Demon använder zippopotam.us (gratis, ingen nyckel). Postnumret ska skickas utan mellanslag, t.ex. "11121".',
    apiMethod: 'GET',
    apiUrl: 'https://api.zippopotam.us/se/{postal_code}',
    exampleQuery: 'Vilken ort har postnummer 11121?',
    parameters: [
      { name: 'postal_code', type: 'string', description: 'Svenskt postnummer utan mellanslag, 5 siffror, t.ex. "11121"', isRequired: true }
    ]
  }
]

const useTemplate = (tpl: DemoTemplate) => {
  editingTool.value = null
  form.value = {
    name: tpl.name,
    displayName: tpl.displayName,
    description: tpl.description,
    apiUrl: tpl.apiUrl,
    apiMethod: tpl.apiMethod,
    parameters: tpl.parameters.map(p => ({
      name: p.name,
      type: p.type,
      description: p.description,
      isRequired: p.isRequired
    })),
    agentIds: agents.value.length > 0 ? [agents.value[0].id] : []
  }
  headersText.value = '{}'
  showCreateModal.value = true
  activeTab.value = 'tools'
}

const form = ref({
  name: '',
  displayName: '',
  description: '',
  apiUrl: '',
  apiMethod: 'GET',
  parameters: [] as ToolParameter[],
  agentIds: [] as number[]
})

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('sv-SE')
}

const getAgentName = (agentId: number): string => {
  const agent = agents.value.find(a => a.id === agentId)
  return agent?.name || `Agent ${agentId}`
}

const fetchTools = async () => {
  loading.value = true
  try {
    const result = await toolsService.getTools()
    if (result.success && result.data) {
      tools.value = result.data
    }
  } catch (error) {
    console.error('Failed to fetch tools:', error)
  } finally {
    loading.value = false
  }
}

const fetchAgents = async () => {
  try {
    const result = await agentsService.getAgents()
    if (Array.isArray(result) && result.length > 0) {
      agents.value = result.map((a: any) => ({
        id: Number(a.id),
        name: a.name
      }))
    }
  } catch (error) {
    console.error('Failed to fetch agents:', error)
  }
}

const addParameter = () => {
  form.value.parameters.push({
    name: '',
    type: 'string',
    description: '',
    isRequired: true
  })
}

const removeParameter = (index: number) => {
  form.value.parameters.splice(index, 1)
}

const editTool = (tool: Tool) => {
  editingTool.value = tool
  form.value = {
    name: tool.name,
    displayName: tool.display_name,
    description: tool.description,
    apiUrl: tool.api_url,
    apiMethod: tool.api_method,
    parameters: (tool.parameters || []).map(p => ({
      name: p.parameter_name || p.name,
      type: p.parameter_type || p.type,
      description: p.description,
      isRequired: p.is_required ?? p.isRequired ?? true
    })),
    agentIds: tool.assigned_agent_ids || []
  }
  headersText.value = JSON.stringify(tool.api_headers || {}, null, 2)
  showCreateModal.value = true
}

const deleteTool = async (tool: Tool) => {
  if (!confirm(`Är du säker på att du vill ta bort "${tool.display_name}" permanent? Detta kan inte ångras.`)) return
  
  try {
    const result = await toolsService.deleteTool(tool.tool_id)
    if (result.success) {
      tools.value = tools.value.filter(t => t.tool_id !== tool.tool_id)
      alert('Tool raderat permanent!')
    } else {
      alert(`Kunde inte radera tool: ${result.error || result.message || 'Okänt fel'}`)
    }
  } catch (error) {
    console.error('Failed to delete tool:', error)
    alert(`Ett fel uppstod: ${(error as Error).message}`)
  }
}

const saveTool = async () => {
  if (form.value.agentIds.length === 0) {
    alert('Välj minst en agent')
    return
  }
  
  saving.value = true
  try {
    let apiHeaders = {}
    try {
      apiHeaders = JSON.parse(headersText.value)
    } catch (e) {
      alert('Ogiltigt JSON i Headers')
      saving.value = false
      return
    }

    const toolData = {
      name: form.value.name,
      displayName: form.value.displayName,
      description: form.value.description,
      apiUrl: form.value.apiUrl,
      apiMethod: form.value.apiMethod,
      apiHeaders,
      parameters: form.value.parameters,
      agentIds: form.value.agentIds
    }

    if (editingTool.value) {
      const result = await toolsService.updateTool(editingTool.value.tool_id, toolData)
      if (result.success) {
        await fetchTools()
        closeModal()
      }
    } else {
      const result = await toolsService.createTool(toolData)
      if (result.success) {
        await fetchTools()
        closeModal()
      }
    }
  } catch (error) {
    console.error('Failed to save tool:', error)
    alert('Kunde inte spara tool')
  } finally {
    saving.value = false
  }
}

const closeModal = () => {
  showCreateModal.value = false
  editingTool.value = null
  form.value = {
    name: '',
    displayName: '',
    description: '',
    apiUrl: '',
    apiMethod: 'GET',
    parameters: [],
    agentIds: agents.value.length > 0 ? [agents.value[0].id] : []
  }
  headersText.value = '{}'
}

onMounted(() => {
  fetchAgents()
  fetchTools()
})
</script>

